import React from "react";
import { Box, RadioGroup } from "@mui/material";
import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader, DotLegalRadioButton } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useTranslation } from "@localization/useTranslation.ts";
import DotLegalStepHeader from "@common/components/dotLegalStepHeader/DotLegalStepHeader.tsx";
import LegalEntityTable from "../../../legalEntity/legalEntityTable/LegalEntityTable.tsx";
import { useLegalEntitySubContractorsField } from "./LegalEntitySubContractorsField.hooks.ts";

export interface LegalEntitySubContractorsFieldProps {
    legalEntityId: string;
    legalEntityName?: string;
    subContractorIds: Array<string>;
    shouldAutoAddSubContractors: boolean;
    onShouldAutoAddSubContractorsChange: (shouldAutoAdd: boolean) => void;
    // Sub contractors are stored by their own endpoints, so the page reloads the legal entity afterwards: removing
    // the last one also clears shouldAutoAddSubContractors, which only the server knows about. Left out by the
    // template editor preview, which is what keeps the table read only there.
    onSubContractorsChanged?: () => void;
    readOnly?: boolean;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
}

function LegalEntitySubContractorsField(props: LegalEntitySubContractorsFieldProps) {
    const { translateString } = useTranslation();
    const {
        selectableLegalEntities,
        isLoading,
        isEditable,
        onSubContractorsChange,
        subContractorForDeletion,
        onRemoveSubContractorConfirmed,
        closeRemoveDialog,
        alsoRemoveFromProcessingActivities,
        setAlsoRemoveFromProcessingActivities,
    } = useLegalEntitySubContractorsField(props);

    const showAutoAddChoice = props.isPreview || props.subContractorIds.length > 0;

    return (
        <>
            <DotLegalStepHeader record={false} topMargin bottomMargin>
                {translateString("subContractorsStepInfo")}
            </DotLegalStepHeader>
            <LegalEntityTable
                isLoading={isLoading}
                legalEntities={selectableLegalEntities}
                selectedEntities={props.subContractorIds}
                showAddNewEntityButton={false}
                onLegalEntitiesChange={onSubContractorsChange}
                disabled={props.disabled}
                addGroupEntityDialogHeader={translateString("addSubContractor")}
                addBtnText={translateString("addSubContractor")}
                noLegalEntitiesSelectedHeader={translateString("subContractorsStepEmpty")}
                checkBoxText={isEditable ? <Trans i18nKey="addSubContractorInfo" values={{ supplier: props.legalEntityName }} /> : undefined}
                errorText={props.errorText}
            />

            {showAutoAddChoice && (
                <Box sx={{ marginTop: 5, display: "flex", alignItems: "center", flexDirection: "column" }}>
                    <DotLegalHeader center headerStyle="small">
                        {translateString("subContractorsAutomaticAddedHeader")}
                    </DotLegalHeader>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 3, marginTop: 2 }}>
                        <DotLegalHeader headerStyle={"small"} fontWeight={100} marginBottom={0}>
                            {translateString("subContractorsAutomaticAdded")}
                        </DotLegalHeader>

                        <RadioGroup
                            row
                            value={props.shouldAutoAddSubContractors ? 1 : 0}
                            onChange={
                                props.readOnly
                                    ? () => {}
                                    : (event) => props.onShouldAutoAddSubContractorsChange(Number((event.target as HTMLInputElement).value) === 1)
                            }
                        >
                            <DotLegalRadioButton value={1} labelPlacement="end" label={translateString("yes")} disabled={props.disabled} />
                            <DotLegalRadioButton value={0} label={translateString("no")} disabled={props.disabled} />
                        </RadioGroup>
                    </Box>
                </Box>
            )}

            {subContractorForDeletion && (
                <DotLegalDialog
                    buttonOkText={translateString("remove")}
                    open
                    onDialogClose={closeRemoveDialog}
                    onOkClick={() => onRemoveSubContractorConfirmed()}
                    header={translateString("removeSecondarySupplier")}
                    size="sm"
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <DotLegalHeader headerStyle={"small"} fontWeight={400} center>
                            <Trans
                                i18nKey="removeSubContractorInfo"
                                values={{ subContractor: subContractorForDeletion.name, supplier: props.legalEntityName }}
                            />
                        </DotLegalHeader>
                        <DotLegalCheckbox
                            checked={alsoRemoveFromProcessingActivities}
                            label={translateString("removeSubContractor")}
                            onChange={(confirm) => setAlsoRemoveFromProcessingActivities(confirm)}
                        />
                    </Box>
                </DotLegalDialog>
            )}
        </>
    );
}

export default LegalEntitySubContractorsField;
